<template>
  <base-material-card
    icon="mdi-bell-alert"
    title="Notices Report"
    class="px-5 py-3"
  >
    <v-data-table
      :headers="headers"
      :items="notices"
      :loading="loading"
      item-key="id"
    >
      <template v-slot:item.issuedDate="{ item }">
        {{ formatDate(item.issuedDate) }}
      </template>

      <template v-slot:item.expirationDate="{ item }">
        {{ formatDate(item.expirationDate) }}
      </template>

      <template v-slot:item.noticeState="{ item }">
        <v-chip
          :color="getStateColor(item.noticeState)"
          small
        >
          {{ item.noticeState }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          color="success"
          @click="viewLetter(item)"
        >
          View Letter
        </v-btn>
      </template>
    </v-data-table>

    <!-- Letter Dialog -->
    <v-dialog
      v-model="letterDialog"
      max-width="900px"
    >
      <v-card>
        <v-card-title class="headline">
          Notice Letter
          <v-spacer />
          <v-btn
            icon
            @click="print"
          >
            <v-icon>mdi-printer</v-icon>
          </v-btn>
          <v-btn
            icon
            :disabled="!canEdit"
            @click="toggleEdit"
          >
            <v-icon>{{ isEditing ? 'mdi-check' : 'mdi-pencil' }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <template v-if="isEditing">
            <tiptap-vuetify
              v-model="editedBody"
              :extensions="extensions"
              :toolbar-attributes="{ color: 'black' }"
              :card-props="{ flat: true }"
              placeholder="Notice content..."
            />
          </template>
          <template v-else>
            <div
              class="notice-content pa-4"
              v-html="selectedNotice.body"
            />
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="closeLetter"
          >
            Close
          </v-btn>
          <v-btn
            v-if="isEditing"
            color="primary"
            @click="saveNotice"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
  import { Q_GET_NOTICES, M_UPDATE_NOTICE } from '@/gql/notice'
  import { TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History } from 'tiptap-vuetify'
  import moment from 'moment'

  export default {
    name: 'NoticesReport',

    components: {
      TiptapVuetify,
    },

    data: () => ({
      loading: false,
      notices: [],
      headers: [
        { text: 'Member', value: 'member.fullName' },
        { text: 'Notice Type', value: 'noticeType.name' },
        { text: 'Offense Type', value: 'offenseType.name' },
        { text: 'Issued Date', value: 'issuedDate' },
        { text: 'Expiration Date', value: 'expirationDate' },
        { text: 'Status', value: 'noticeState' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      letterDialog: false,
      selectedNotice: {},
      isEditing: false,
      editedBody: '',
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3],
          },
        }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
    }),

    computed: {
      canEdit () {
        return this.selectedNotice?.noticeState === 'approved'
      },
    },

    created () {
      this.fetchNotices()
    },

    methods: {
      async fetchNotices () {
        this.loading = true
        try {
          const { data } = await this.$apollo.query({
            query: Q_GET_NOTICES,
            variables: {
              where: {},
            },
          })
          this.notices = data.notices
        } catch (error) {
          console.error('Error fetching notices:', error)
          this.$toast.error('Failed to load notices')
        } finally {
          this.loading = false
        }
      },

      formatDate (date) {
        return moment(date).format('MMM DD, YYYY')
      },

      getStateColor (state) {
        const colors = {
          approved: 'success',
          sent: 'info',
          acknowledged: 'primary',
        }
        return colors[state] || 'grey'
      },

      viewLetter (notice) {
        this.selectedNotice = notice
        this.editedBody = notice.body
        this.letterDialog = true
        this.isEditing = false
      },

      toggleEdit () {
        if (this.isEditing) {
          this.saveNotice()
        } else {
          this.isEditing = true
        }
      },

      async saveNotice () {
        try {
          const { data } = await this.$apollo.mutate({
            mutation: M_UPDATE_NOTICE,
            variables: {
              id: this.selectedNotice.id,
              body: this.editedBody,
            },
          })

          const index = this.notices.findIndex(n => n.id === this.selectedNotice.id)
          this.notices[index].body = data.updateNotice.body
          this.selectedNotice.body = data.updateNotice.body
          this.isEditing = false
          this.$toast.success('Notice updated successfully')
        } catch (error) {
          console.error('Error updating notice:', error)
          this.$toast.error('Failed to update notice')
        }
      },

      closeLetter () {
        this.letterDialog = false
        this.selectedNotice = {}
        this.editedBody = ''
        this.isEditing = false
      },

      print () {
        const printContent = this.selectedNotice.body
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
        <html>
          <head>
            <title>Notice Letter</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `)
        printWindow.document.close()
        printWindow.print()
      },
    },
  }
</script>

<style>
.notice-content {
  min-height: 300px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
